import {
  AddRounded,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  CloseRounded,
  DragIndicator,
  RemoveRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  alpha,
  Box,
  Button as MuiButton,
  Card,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Button as RaButton, useDataProvider, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';
import { PermissionGuard } from 'src/routes/components';
import { fShortenNumber } from 'src/utils/format-number';
import { Resources } from '../resource';
import CandidatesPanel from './components/CandidatesPanel';
import { ChangePercent, PairName, SymbolAvatar, thinScrollbarSx } from './components/shared';
import { HotSymbolCandidate, HotSymbolPinsData } from './types';
import './init';

// 候选池上游每 5 分钟更新一次成交量，页面停留期间静默同步（只刷候选，不动草稿）
const CANDIDATES_REFRESH_INTERVAL = 5 * 60 * 1000;

// 左侧面板的置顶行（含已下架兜底：symbol 不在候选池时按 pins 里的信息展示）
interface DraftPinRow {
  symbol: string;
  displaySymbol: string;
  dex: string;
  volume?: number;
  changPxPercent?: number;
  isListed: boolean;
}

function KpiCard({
  label,
  value,
  caption,
  highlighted = false,
}: {
  label: string;
  value: ReactNode;
  caption: string;
  highlighted?: boolean;
}) {
  return (
    <Card
      sx={{
        p: 3,
        ...(highlighted && {
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        }),
      }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ my: 0.5 }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography variant="h3">{value}</Typography>
        ) : (
          value
        )}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    </Card>
  );
}

function SortablePinRow({
  row,
  index,
  count,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  row: DraftPinRow;
  index: number;
  count: number;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onRemove: (symbol: string) => void;
}) {
  const translate = useTranslate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.symbol,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{
          px: 1.5,
          py: 1.25,
          border: '1px solid',
          borderRadius: 1.5,
          borderColor: row.isListed
            ? (theme) => alpha(theme.palette.primary.main, 0.32)
            : 'warning.main',
          bgcolor: (theme) => alpha(theme.palette.primary.main, row.isListed ? 0.04 : 0),
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ width: 24, textAlign: 'center', color: 'primary.main' }}
        >
          {index + 1}
        </Typography>
        <IconButton
          size="small"
          sx={{ cursor: 'grab', color: 'text.disabled' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
        <SymbolAvatar symbol={row.symbol} label={row.displaySymbol} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PairName displaySymbol={row.displaySymbol} dex={row.dex} />
          {row.isListed && row.volume !== undefined ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {`$${fShortenNumber(row.volume)}`}
              </Typography>
              <ChangePercent value={row.changPxPercent ?? 0} />
            </Stack>
          ) : null}
        </Box>
        {row.isListed ? (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={translate('module.hotSymbols.pin')}
          />
        ) : (
          <Tooltip title={translate('module.hotSymbols.delistedTip')}>
            <Chip size="small" color="warning" label={translate('module.hotSymbols.delisted')} />
          </Tooltip>
        )}
        <PermissionGuard
          resource="hot-symbols"
          action="update"
          fallback={<></>}
          loadingFallback={<></>}
        >
          <IconButton
            size="small"
            disabled={index === 0}
            onClick={() => onMoveUp(index)}
            aria-label={translate('module.hotSymbols.moveUp')}
          >
            <ArrowUpwardRounded fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={index === count - 1}
            onClick={() => onMoveDown(index)}
            aria-label={translate('module.hotSymbols.moveDown')}
          >
            <ArrowDownwardRounded fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onRemove(row.symbol)}
            aria-label={translate('module.hotSymbols.removePin')}
          >
            <CloseRounded fontSize="small" />
          </IconButton>
        </PermissionGuard>
      </Stack>
    </div>
  );
}

function AutoFillRow({
  candidate,
  position,
  onPin,
}: {
  candidate: HotSymbolCandidate;
  position: number;
  onPin: (symbol: string) => void;
}) {
  const translate = useTranslate();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ px: 1.5, py: 1.25, borderRadius: 1.5, bgcolor: 'background.neutral' }}
    >
      <Typography
        variant="subtitle2"
        sx={{ width: 24, textAlign: 'center', color: 'text.secondary' }}
      >
        {position}
      </Typography>
      <SymbolAvatar symbol={candidate.symbol} label={candidate.displaySymbol} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <PairName displaySymbol={candidate.displaySymbol} dex={candidate.dex} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {`$${fShortenNumber(candidate.volume)}`}
          </Typography>
          <ChangePercent value={candidate.changPxPercent} />
        </Stack>
      </Box>
      {/* 「自动」为弱化标签：灰底灰字，避免默认填充色喧宾夺主 */}
      <Chip
        size="small"
        label={translate('module.hotSymbols.auto')}
        sx={{ color: 'text.secondary', bgcolor: 'action.selected' }}
      />
      <MuiButton
        size="small"
        variant="outlined"
        color="success"
        onClick={() => onPin(candidate.symbol)}
      >
        {translate('module.hotSymbols.pin')}
      </MuiButton>
    </Stack>
  );
}

const View = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [candidates, setCandidates] = useState<HotSymbolCandidate[]>([]);
  const [candidateTotal, setCandidateTotal] = useState(0);
  const [pinsData, setPinsData] = useState<HotSymbolPinsData | null>(null);
  const [serverPins, setServerPins] = useState<string[]>([]);
  const [draftPins, setDraftPins] = useState<string[]>([]);
  // 栏位数纳入草稿态：XBIT-2994 起可自由调整，保存时随 pinned 一起提交
  const [serverSlotCount, setServerSlotCount] = useState(7);
  const [draftSlotCount, setDraftSlotCount] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  const slotCount = draftSlotCount;
  // 栏位数不能小于当前置顶数（上游校验 cannot pin N symbols, only M slots）
  const minSlotCount = Math.max(1, draftPins.length);

  const loadCandidates = useCallback(async () => {
    const response = await dataProvider.getList(Resources.hotSymbols, {
      filter: {},
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'volume', order: 'DESC' },
    });
    setCandidates(response?.data ?? []);
    setCandidateTotal(response?.total ?? response?.data?.length ?? 0);
  }, [dataProvider]);

  const applyPinsData = useCallback((data: HotSymbolPinsData) => {
    setPinsData(data);
    const symbols = (data?.pins ?? []).map((pin) => pin.symbol);
    setServerPins(symbols);
    setDraftPins(symbols);
    const savedSlotCount = data?.slotCount ?? 7;
    setServerSlotCount(savedSlotCount);
    setDraftSlotCount(savedSlotCount);
  }, []);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const [candidatesResponse, pinsResponse] = await Promise.all([
          dataProvider.getList(Resources.hotSymbols, {
            filter: {},
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'volume', order: 'DESC' },
          }),
          dataProvider.getOne(Resources.hotSymbols, { id: 'pins' }),
        ]);
        if (!active) return;
        setCandidates(candidatesResponse?.data ?? []);
        setCandidateTotal(candidatesResponse?.total ?? candidatesResponse?.data?.length ?? 0);
        applyPinsData(pinsResponse?.data);
      } catch (error: any) {
        if (!active) return;
        notify(error?.body?.message || error?.message || translate('module.hotSymbols.loadError'), {
          type: 'error',
        });
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [dataProvider, notify, translate, applyPinsData]);

  // 页面停留期间每 5 分钟静默刷新候选池（保留草稿不动），失败忽略等下个周期
  useEffect(() => {
    const timer = setInterval(() => {
      loadCandidates().catch(() => {});
    }, CANDIDATES_REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [loadCandidates]);

  const candidateMap = useMemo(
    () => new Map(candidates.map((item) => [item.symbol, item])),
    [candidates]
  );
  const draftSet = useMemo(() => new Set(draftPins), [draftPins]);

  const pinRows: DraftPinRow[] = useMemo(
    () =>
      draftPins.map((symbol) => {
        const candidate = candidateMap.get(symbol);
        if (candidate) {
          return {
            symbol,
            displaySymbol: candidate.displaySymbol,
            dex: candidate.dex,
            volume: candidate.volume,
            changPxPercent: candidate.changPxPercent,
            isListed: true,
          };
        }
        // 不在候选池 => 已下架，仍展示并提示移除
        const pin = pinsData?.pins?.find((item) => item.symbol === symbol);
        return { symbol, displaySymbol: pin?.displaySymbol ?? symbol, dex: '', isListed: false };
      }),
    [draftPins, candidateMap, pinsData]
  );

  // 自动补位 = 候选池剔除草稿置顶后按原成交量顺序取前 N 个（候选池本身已降序，无需重排）
  const autoRows = useMemo(
    () =>
      candidates
        .filter((item) => !draftSet.has(item.symbol))
        .slice(0, Math.max(0, slotCount - draftPins.length)),
    [candidates, draftSet, slotCount, draftPins.length]
  );

  const isChanged = useMemo(
    () =>
      JSON.stringify(draftPins) !== JSON.stringify(serverPins) ||
      draftSlotCount !== serverSlotCount,
    [draftPins, serverPins, draftSlotCount, serverSlotCount]
  );

  // 置顶不设上限：超出当前栏位数时栏位自动 +1 跟上（XBIT-2994）
  const handlePin = (symbol: string) => {
    if (draftPins.includes(symbol)) return;
    const next = [...draftPins, symbol];
    setDraftPins(next);
    if (next.length > draftSlotCount) setDraftSlotCount(next.length);
  };

  const handleUnpin = (symbol: string) => {
    setDraftPins((prev) => prev.filter((item) => item !== symbol));
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    setDraftPins((prev) => {
      const next = index + direction;
      if (next < 0 || next >= prev.length) return prev;
      return arrayMove(prev, index, next);
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setDraftPins((prev) => {
        const oldIndex = prev.indexOf(`${active.id}`);
        const newIndex = prev.indexOf(`${over.id}`);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleDiscard = () => {
    setDraftPins(serverPins);
    setDraftSlotCount(serverSlotCount);
    notify(translate('module.hotSymbols.discarded'), { type: 'info' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 全量覆盖；响应即重算后的最终状态，直接回填无需再查。栏位数兜底不小于置顶数
      const response = await dataProvider.update(Resources.hotSymbols, {
        id: 'pins',
        data: { pinned: draftPins, slotCount: Math.max(minSlotCount, draftSlotCount) },
        previousData: pinsData ?? {},
      });
      applyPinsData(response?.data);
      notify(translate('module.hotSymbols.saveSuccess'), { type: 'success' });
      // 保存后静默刷新候选池，同步最新成交量与置顶状态
      loadCandidates().catch(() => {});
    } catch (error: any) {
      notify(error?.body?.message || error?.message || translate('module.hotSymbols.saveError'), {
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const updatedAtText = pinsData?.updatedAt
    ? dayjs(Number(pinsData.updatedAt)).format('YYYY-MM-DD HH:mm:ss')
    : '-';

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={translate('resource.hotSymbols')}
        links={[
          { name: translate('resource.sectionContentOps') },
          { name: translate('resource.hotSymbols') },
        ]}
        action={
          <Stack direction="row" spacing={2}>
            <PermissionGuard
              resource="hot-symbols"
              action="update"
              fallback={<></>}
              loadingFallback={<></>}
            >
              <MuiButton
                variant="outlined"
                color="inherit"
                onClick={handleDiscard}
                disabled={loading || saving || !isChanged}
              >
                {translate('module.hotSymbols.discard')}
              </MuiButton>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleSave}
                loading={saving}
                disabled={loading || !isChanged}
              >
                {translate('module.hotSymbols.save')}
              </LoadingButton>
            </PermissionGuard>
            <PermissionGuard
              resource="audit-logs"
              action="read"
              fallback={<></>}
              loadingFallback={<></>}
            >
              <RaButton
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => setAuditLogOpen(true)}
                label={translate('resource.auditLogs')}
              >
                <Iconify icon="ph:note" />
              </RaButton>
            </PermissionGuard>
          </Stack>
        }
        sx={{ mb: 1 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('module.hotSymbols.subtitle', { count: slotCount })}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>{translate('module.hotSymbols.rulesTitle')}</AlertTitle>
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {[1, 2, 3, 4].map((rule) => (
            <Typography key={rule} component="li" variant="body2">
              {translate(`module.hotSymbols.rule${rule}`, { count: slotCount })}
            </Typography>
          ))}
        </Box>
      </Alert>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          mb: 3,
        }}
      >
        <KpiCard
          label={translate('module.hotSymbols.kpiPinned')}
          highlighted
          value={
            <Stack direction="row" spacing={0.75} alignItems="baseline">
              <Typography
                variant="h3"
                component="span"
                sx={{ color: 'primary.main', fontWeight: 700 }}
              >
                {draftPins.length}
              </Typography>
              <Typography variant="h5" component="span" sx={{ color: 'text.disabled' }}>
                {`/ ${slotCount}`}
              </Typography>
            </Stack>
          }
          caption={translate('module.hotSymbols.kpiSlotAdjustable')}
        />
        <KpiCard
          label={translate('module.hotSymbols.kpiAuto')}
          value={autoRows.length}
          caption={translate('module.hotSymbols.kpiAutoDesc')}
        />
        <KpiCard
          label={translate('module.hotSymbols.kpiCandidates')}
          value={candidateTotal}
          caption={translate('module.hotSymbols.kpiCandidatesDesc')}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        }}
      >
        <Card sx={{ p: 3, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            <Typography variant="h6">{translate('module.hotSymbols.slotsTitle')}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                label={translate('module.hotSymbols.slotsBadge', {
                  used: draftPins.length,
                  total: slotCount,
                })}
                sx={(theme) => ({
                  fontWeight: 600,
                  // 深色模式下用白字 + 更高透明度底色，避免暗底上看不清；浅色模式保持主题色
                  color: theme.palette.mode === 'dark' ? 'common.white' : 'primary.main',
                  bgcolor: alpha(
                    theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.28 : 0.16
                  ),
                })}
              />
              <PermissionGuard
                resource="hot-symbols"
                action="update"
                fallback={<></>}
                loadingFallback={<></>}
              >
                <Typography variant="caption" color="text.secondary">
                  {translate('module.hotSymbols.slotCountLabel')}
                </Typography>
                <Tooltip
                  title={
                    slotCount <= minSlotCount ? translate('module.hotSymbols.slotCountMinHint') : ''
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={loading || slotCount <= minSlotCount}
                      onClick={() => setDraftSlotCount((prev) => Math.max(minSlotCount, prev - 1))}
                    >
                      <RemoveRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <TextField
                  size="small"
                  type="number"
                  disabled={loading}
                  value={slotCount}
                  onChange={(event) => {
                    const value = parseInt(event.target.value, 10);
                    if (Number.isFinite(value)) setDraftSlotCount(value);
                  }}
                  onBlur={() => setDraftSlotCount((prev) => Math.max(minSlotCount, prev))}
                  inputProps={{
                    min: minSlotCount,
                    style: { textAlign: 'center', width: 48, padding: '4px 2px' },
                  }}
                />
                <IconButton
                  size="small"
                  disabled={loading}
                  onClick={() => setDraftSlotCount((prev) => prev + 1)}
                >
                  <AddRounded fontSize="small" />
                </IconButton>
              </PermissionGuard>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {translate('module.hotSymbols.slotsHint')}
          </Typography>
          {candidateTotal > 0 && slotCount > candidateTotal ? (
            <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
              {translate('module.hotSymbols.slotCountOverPool', { count: candidateTotal })}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {`${translate('module.hotSymbols.lastUpdated')}: ${updatedAtText}`}
            {pinsData?.operator
              ? ` · ${translate('module.hotSymbols.operator')}: ${pinsData.operator}`
              : ''}
          </Typography>

          {/* 列表体内滚填满剩余高度；绝对定位让其不把卡片撑高，卡片高度以右侧候选面板的固定高度为基准（栅格等高拉伸） */}
          <Box sx={{ flex: 1, minHeight: 420, position: 'relative', mt: 2 }}>
            <Stack
              spacing={1.5}
              sx={(theme) => ({
                position: 'absolute',
                inset: 0,
                overflowY: 'auto',
                pr: 0.5,
                ...thinScrollbarSx(theme),
              })}
            >
              {loading ? (
                <Typography variant="body2" color="text.secondary">
                  {translate('module.hotSymbols.loading')}
                </Typography>
              ) : (
                <>
                  {pinRows.length === 0 ? (
                    <Box
                      sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 3 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {translate('module.hotSymbols.emptyPins')}
                      </Typography>
                    </Box>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      modifiers={[restrictToVerticalAxis]}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={draftPins} strategy={verticalListSortingStrategy}>
                        <Stack spacing={1.5}>
                          {pinRows.map((row, index) => (
                            <SortablePinRow
                              key={row.symbol}
                              row={row}
                              index={index}
                              count={pinRows.length}
                              onMoveUp={(i) => handleMove(i, -1)}
                              onMoveDown={(i) => handleMove(i, 1)}
                              onRemove={handleUnpin}
                            />
                          ))}
                        </Stack>
                      </SortableContext>
                    </DndContext>
                  )}

                  {autoRows.length > 0 ? (
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ my: 0.5 }}>
                      <Box sx={{ flex: 1, borderBottom: '1px dashed', borderColor: 'divider' }} />
                      <Typography variant="caption" color="text.secondary">
                        {translate('module.hotSymbols.autoFillDivider')}
                      </Typography>
                      <Box sx={{ flex: 1, borderBottom: '1px dashed', borderColor: 'divider' }} />
                    </Stack>
                  ) : null}

                  {autoRows.map((candidate, index) => (
                    <AutoFillRow
                      key={candidate.symbol}
                      candidate={candidate}
                      position={pinRows.length + index + 1}
                      onPin={handlePin}
                    />
                  ))}
                </>
              )}
            </Stack>
          </Box>
        </Card>

        <CandidatesPanel
          candidates={candidates}
          total={candidateTotal}
          draftPins={draftPins}
          onPin={handlePin}
        />
      </Box>

      <AuditLogDialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        resource="hot-symbols"
        title={`${translate('resource.auditLogs')} - ${translate('resource.hotSymbols')}`}
      />
    </DashboardContent>
  );
};

export default View;
